import { notifications } from "@marketplace/ui/notifications";
import { useAtom } from "jotai";
import { useEffect } from "react";
import {
  type SubmitHandler,
  useForm,
  type UseFormReturn
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { automationSetupNotifyModalAtom } from "../../atoms/automation-setup-notify-modal-atom";
import type { SetupAutomationNotifyRequestDto } from "../../dtos/setup-automation-notify-request-dto";
import type { AutomationListItem } from "../../types";
import { useSetupAutomationNotifyMutation } from "../mutations/use-setup-automation-notify-mutation";
import { useUpdateAutomationNotifyMutation } from "../mutations/use-update-automation-notify-mutation";
import { useGetAutomationNotifySetupQuery } from "../queries/use-get-automation-notify-setup-query";

export type SetupNotifyFormValues = {
  is_notify_buyer: boolean;
  is_notify_superadmin: boolean;
  is_notify_seller: boolean;
  active_custom: boolean;
  custom_notify: Array<{
    type: string;
    subject: string;
    to_list: string;
    cc_list: string;
    template: string;
    metadata: string;
    is_active: boolean;
  }>;
};

type UseSetupAutomationNotifyFormReturn = {
  formMethods: UseFormReturn<SetupNotifyFormValues>;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  handleClose: () => void;
  modalState: { visible: boolean; automation: AutomationListItem | null };
  isPending: boolean;
  isFetching: boolean;
};

export const useSetupAutomationNotifyForm =
  (): UseSetupAutomationNotifyFormReturn => {
    const { t } = useTranslation("product", {
      keyPrefix: "automation.notify"
    });
    const [modalState, setModalState] = useAtom(automationSetupNotifyModalAtom);
    const { mutate: setupNotify, isPending: isSetupPending } =
      useSetupAutomationNotifyMutation();
    const { mutate: updateNotify, isPending: isUpdatePending } =
      useUpdateAutomationNotifyMutation();

    const isPending = isSetupPending || isUpdatePending;

    const { data: notifySetupDataRes, isFetching } =
      useGetAutomationNotifySetupQuery({
        id: modalState.automation?.product_id,
        options: {
          enabled: modalState.visible && !!modalState.automation?.product_id,
          retry: false
        }
      });
    const notifySetupData = notifySetupDataRes?.data;

    const formMethods = useForm<SetupNotifyFormValues>({
      defaultValues: {
        is_notify_buyer: false,
        is_notify_superadmin: false,
        is_notify_seller: false,
        active_custom: false,
        custom_notify: []
      }
    });

    const { reset, handleSubmit } = formMethods;

    useEffect(() => {
      if (modalState.visible && !isFetching) {
        if (notifySetupData) {
          reset({
            is_notify_buyer: notifySetupData.is_notify_buyer,
            is_notify_superadmin: notifySetupData.is_notify_superadmin,
            is_notify_seller: notifySetupData.is_notify_seller,
            active_custom: !!(
              notifySetupData.custom_notify &&
              notifySetupData.custom_notify.length > 0
            ),
            custom_notify:
              notifySetupData.custom_notify?.map((item) => ({
                type: item.type,
                subject: item.subject,
                to_list: item.to_list,
                cc_list: item.cc_list || "",
                template: item.template,
                metadata:
                  item.metadata && Object.keys(item.metadata).length > 0
                    ? JSON.stringify(item.metadata, null, 2)
                    : "{}",
                is_active: item.is_active
              })) || []
          });
        } else {
          reset({
            is_notify_buyer: false,
            is_notify_superadmin: false,
            is_notify_seller: false,
            active_custom: false,
            custom_notify: []
          });
        }
      }
    }, [modalState.visible, isFetching, notifySetupData, reset]);

    const handleClose = () => {
      setModalState({ visible: false, automation: null });
    };

    const onSubmitHandler: SubmitHandler<SetupNotifyFormValues> = (data) => {
      if (!modalState.automation) return;

      let hasError = false;
      const custom_notify = data.active_custom
        ? data.custom_notify.map((item) => {
            let parsedMetadata: Record<string, string> = {};
            try {
              if (item.metadata) parsedMetadata = JSON.parse(item.metadata);
            } catch {
              notifications.show({
                message: t("invalidJsonMetadata"),
                variant: "error"
              });
              hasError = true;
            }
            return {
              type: item.type,
              subject: item.subject,
              to_list: item.to_list,
              cc_list: item.cc_list || undefined,
              template: item.template,
              metadata: parsedMetadata,
              is_active: item.is_active
            };
          })
        : [];

      if (hasError) return;

      const payload: SetupAutomationNotifyRequestDto = {
        product_id: modalState.automation?.product_id,
        is_notify_buyer: data.is_notify_buyer,
        is_notify_superadmin: data.is_notify_superadmin,
        is_notify_seller: data.is_notify_seller,
        custom_notify
      };

      const isUpdate = !!notifySetupData;

      if (isUpdate) {
        updateNotify(
          { payload },
          {
            onSuccess: () => {
              handleClose();
            }
          }
        );
      } else {
        setupNotify(
          { payload },
          {
            onSuccess: () => {
              handleClose();
            }
          }
        );
      }
    };

    const onSubmit = handleSubmit(onSubmitHandler);

    return {
      formMethods,
      onSubmit,
      handleClose,
      modalState,
      isPending,
      isFetching
    };
  };
